import { createTRPCProxyClient } from "@trpc/client";
import type { AppRouter } from "~/../../server/src/api/root";
import { api } from "~/api";
import type { DifyUserType } from "shared/Schemas";
import type {
  DifyAPITestJob,
  DifyAPITestJobStatus,
} from "~/components/difyAPITest/difyAPITest.types";
import { extractMessageId, isMessageEndChunk } from "~/utils/difyAPITest/difyAPITestMessageUtils";

type JobExecutionResult = {
  status: "completed" | "error" | "cancelled";
};

type ExecuteReceptionJobParams = {
  runId: string;
  job: DifyAPITestJob;
  user: DifyUserType;
  wsClient: ReturnType<typeof createTRPCProxyClient<AppRouter>>;
  timeoutMs: number;
  isRunCancelled: () => boolean;
  onStatusChange: (status: DifyAPITestJobStatus) => void;
  onChunk: (chunk: string) => void;
  onCompleted: (messageId: string) => void;
  onError: (message: string) => void;
  onCancelled: () => void;
  onSessionBound: (runId: string, jobId: string, sessionId: string) => void;
  onSessionReleased: (runId: string, jobId: string) => void;
};

/**
 * 単一ジョブを実行する
 * ws購読からapi実行までの一連の流れを管理する
 * @param params 実行パラメータ
 * @return 実行結果
 */
export const executeReceptionJob = async ({
  runId,
  job,
  user,
  wsClient,
  timeoutMs,
  isRunCancelled,
  onStatusChange,
  onChunk,
  onCompleted,
  onError,
  onCancelled,
  onSessionBound,
  onSessionReleased,
}: ExecuteReceptionJobParams): Promise<JobExecutionResult> => {
  if (isRunCancelled()) {
    onCancelled();
    return { status: "cancelled" };
  }

  onSessionBound(runId, job.jobId, job.sessionId);
  onStatusChange("running");

  try {
    return await new Promise<JobExecutionResult>((resolve) => {
      let settled = false;
      // eslint-disable-next-line -- 再代入を許容するため、constではなくletを使用
      let timeoutId: number | undefined;

      /**
       * ジョブ実行を終了状態へ遷移させる
       * @param result 実行結果
       * @return 終了処理の完了
       */
      const settle = (result: JobExecutionResult) => {
        if (settled) {
          return;
        }

        settled = true;
        window.clearTimeout(timeoutId);
        subscription.unsubscribe();
        resolve(result);
      };

      const subscription = wsClient.socket.subscribe(
        { sessionId: job.sessionId },
        {
          onData: (response) => {
            if (settled || typeof response !== "string") {
              return;
            }

            if (isRunCancelled()) {
              onCancelled();
              settle({ status: "cancelled" });
              return;
            }

            if (isMessageEndChunk(response)) {
              const messageId = extractMessageId(response);
              onCompleted(messageId);
              settle({ status: "completed" });
              return;
            }

            if (response === "error.") {
              onError("Difyからエラー応答を受信しました。");
              settle({ status: "error" });
              return;
            }

            onChunk(response);
          },
          onError: (error) => {
            if (settled) {
              return;
            }

            if (isRunCancelled()) {
              onCancelled();
              settle({ status: "cancelled" });
              return;
            }

            onError(error.message || "WebSocket受信中にエラーが発生しました。");
            settle({ status: "error" });
          },
        }
      );

      timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }

        void api.dify.stopGeneration.mutate({
          sessionId: job.sessionId,
          customerId: "",
        });
        onError("タイムアウトしました。");
        settle({ status: "error" });
      }, timeoutMs);

      api.dify.reception
        .mutate({
          message: job.question,
          userLanguage: "japanese",
          sessionId: job.sessionId,
          user,
        })
        .catch(() => {
          if (settled) {
            return;
          }

          if (isRunCancelled()) {
            onCancelled();
            settle({ status: "cancelled" });
            return;
          }

          onError("Dify API送信に失敗しました。");
          settle({ status: "error" });
        });
    });
  } finally {
    // セッション解放
    onSessionReleased(runId, job.jobId);
  }
};

export type { ExecuteReceptionJobParams, JobExecutionResult };
